import { CardSkeleton } from "@/components/atoms/CardSkeleton/CardSkeleton";
import { ConnectionOwnerNotice } from "@/components/molecules/ConnectionOwnerNotice";
import { GoogleSearchConsoleAddButton } from "@/components/molecules/GoogleSearchConsoleAddButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { IncludeExcludeToggle } from "@/components/ui/include-exclude-toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDashboardUrlParams } from "@/hooks/useDashboardUrlParams";
import { useDomainIntegrations } from "@/hooks/useDomainIntegrations";
import { useGoogleSearchConsoleConnection } from "@/hooks/useGoogleSearchConsoleConnection";
import { useGoogleSearchConsoleData } from "@/hooks/useGoogleSearchConsoleData";
import { useOrgRole } from "@/hooks/useOrgRole";
import { useSonnerToast } from "@/hooks/useSonnerToast";
import { useStore } from "@/store";
import { Loader2 } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

interface GoogleSearchConsoleSectionProps {
  responsiveButtons?: boolean;
  showTitle?: boolean;
  showHeader?: boolean;
  showEmptyState?: boolean;
}

export interface GoogleSearchConsoleSectionRef {
  enterEdit: () => void;
}

const getTimestampMillis = (
  timestamp: Date | { toDate?: () => Date } | null | undefined,
) => {
  if (!timestamp) return 0;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof timestamp.toDate === "function") return timestamp.toDate().getTime();
  return 0;
};

export const GoogleSearchConsoleSection = forwardRef<
  GoogleSearchConsoleSectionRef,
  GoogleSearchConsoleSectionProps
>(function GoogleSearchConsoleSection(
  {
    responsiveButtons = false,
    showTitle = true,
    showHeader = true,
    showEmptyState = true,
  },
  ref,
) {
  const { currentUser, currentDomain, orgId, getIsConnectionOwner, getMemberDisplayName } =
    useStore();
  const { params, clearParams } = useDashboardUrlParams();
  const { toast } = useSonnerToast();
  const { isAdmin } = useOrgRole();
  const { connectNewAccount, disconnectAccount } =
    useGoogleSearchConsoleConnection();
  const userId = useMemo(() => currentUser?.uid || null, [currentUser?.uid]);
  const { accounts, hasConnectedAccounts, getSitesForAccount } =
    useGoogleSearchConsoleData(userId);
  const {
    googleSearchConsoleIntegration,
    updateGoogleSearchConsole,
    setGoogleSearchConsoleEnabled,
    deleteGoogleSearchConsole,
  } = useDomainIntegrations(orgId, currentDomain?.id || null);

  const selectedAccountId = googleSearchConsoleIntegration.selectedAccountId;
  const selectedSiteUrl = googleSearchConsoleIntegration.selectedSiteUrl;
  const hasAccounts = hasConnectedAccounts();
  const isDomainConfigured = !!(selectedAccountId && selectedSiteUrl);
  const [isConnecting, setIsConnecting] = useState(false);
  const [pendingGscEdit, setPendingGscEdit] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftAccountId, setDraftAccountId] = useState<string | undefined>(
    selectedAccountId ?? undefined,
  );
  const [draftSiteUrl, setDraftSiteUrl] = useState<string | undefined>(
    selectedSiteUrl ?? undefined,
  );

  const isConnectionOwner = getIsConnectionOwner(googleSearchConsoleIntegration);
  const connectedByName = getMemberDisplayName(
    googleSearchConsoleIntegration.connectedBy ?? "",
  );

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.account_id === selectedAccountId),
    [accounts, selectedAccountId],
  );
  const selectedSite = useMemo(() => {
    if (!selectedAccountId) return null;
    return getSitesForAccount(selectedAccountId).find(
      (site) => site.site_url === selectedSiteUrl,
    );
  }, [selectedAccountId, selectedSiteUrl, getSitesForAccount]);
  // `accounts` only ever contains the *viewer's* connected accounts, so it is
  // empty for anyone who did not connect this integration -- fall back to it
  // only when the domain doc has no denormalized display string. The site
  // URL itself is already stored on the domain doc, so it needs no
  // denormalized twin.
  const accountDisplayName =
    googleSearchConsoleIntegration.connectedAccountEmail ??
    selectedAccount?.account_email ??
    selectedAccount?.account_name ??
    null;
  const siteDisplayName = selectedSiteUrl ?? selectedSite?.site_url ?? null;

  const mostRecentAccount = useMemo(() => {
    if (accounts.length === 0) return null;
    if (accounts.length === 1) return accounts[0];
    return accounts.reduce((mostRecent, account) => {
      const mostRecentTime = mostRecent.updated_at || mostRecent.created_at;
      const accountTime = account.updated_at || account.created_at;
      if (!accountTime) return mostRecent;
      if (!mostRecentTime) return account;
      return getTimestampMillis(accountTime) > getTimestampMillis(mostRecentTime)
        ? account
        : mostRecent;
    });
  }, [accounts]);

  useEffect(() => {
    if (!isEditing) {
      setDraftAccountId(selectedAccountId ?? undefined);
      setDraftSiteUrl(selectedSiteUrl ?? undefined);
    }
  }, [isEditing, selectedAccountId, selectedSiteUrl]);

  useEffect(() => {
    if (params.gscEdit) {
      setPendingGscEdit(true);
      clearParams(["gscEdit"]);
    }
  }, [params.gscEdit, clearParams]);

  useEffect(() => {
    if (pendingGscEdit && hasAccounts) {
      const accountToSelect =
        selectedAccountId ?? mostRecentAccount?.account_id;
      setDraftAccountId(accountToSelect ?? undefined);
      setDraftSiteUrl(selectedSiteUrl ?? undefined);
      setIsEditing(true);
      setPendingGscEdit(false);
    }
  }, [
    pendingGscEdit,
    hasAccounts,
    selectedAccountId,
    selectedSiteUrl,
    mostRecentAccount,
  ]);

  const enterEdit = () => {
    setDraftAccountId(selectedAccountId ?? undefined);
    setDraftSiteUrl(selectedSiteUrl ?? undefined);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraftAccountId(selectedAccountId ?? undefined);
    setDraftSiteUrl(selectedSiteUrl ?? undefined);
    setIsEditing(false);
  };

  const saveConnection = async () => {
    if (!draftAccountId || !draftSiteUrl) return;

    // At save time the editor is the owner, so `accounts` is populated for
    // them -- resolve the display string now and denormalize it onto the
    // domain doc so any org member can render it later.
    const account = accounts.find(
      (account) => account.account_id === draftAccountId,
    );

    await updateGoogleSearchConsole(draftAccountId, draftSiteUrl, {
      accountEmail: account?.account_email ?? account?.account_name ?? null,
    });
    setIsEditing(false);
  };

  const handleDisconnect = async () => {
    if (!currentUser?.uid || !currentDomain?.id || !selectedAccountId) return;

    setIsDisconnecting(true);
    try {
      await disconnectAccount(selectedAccountId);
      // Delete rather than null-merge: core's GoogleSearchConsoleDomainIntegration
      // requires selectedAccountId/selectedSiteUrl as non-null strings, so a
      // null-merge would leave a doc that fails validation on every read while
      // still carrying is_enabled/connectedBy. Firestore rules allow `delete`
      // for any org editor.
      await deleteGoogleSearchConsole();
      setShowDisconnectDialog(false);
      toast({
        variant: "success",
        title: "Google Search Console disconnected",
        description: "Your Google Search Console account has been disconnected.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to disconnect",
        description:
          error instanceof Error ? error.message : "An unexpected error occurred",
        closeButton: true,
      });
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectDialog(false);
    }
  };

  useImperativeHandle(ref, () => ({ enterEdit }));

  const buttonClassName = responsiveButtons
    ? "text-sm md:text-base h-9 md:h-10 px-4 md:px-6"
    : "";

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex justify-between items-center">
          {showTitle && (
            <div className="flex items-center space-x-1">
              <h3 className="text-lg font-medium">Google Search Console</h3>
            </div>
          )}
          <div className={`flex gap-2 ${!showTitle ? "ml-auto" : ""}`}>
            {/* A domain someone else already connected has nothing this
                button could legitimately do for a non-owner: "select
                existing"/"connect new" would only kick off the viewer's own
                OAuth flow and any resulting save is rejected by
                firestore.rules (update requires connectedBy == caller uid).
                Hide it rather than offer a dead-end affordance -- the
                ConnectionOwnerNotice below already explains who owns it.
                Anyone below admin is in the same position on an unconnected
                domain: connecting writes the integration doc, which
                firestore.rules only lets an admin create. Editors work
                *through* this connection, never on it. */}
            {isAdmin && (!isDomainConfigured || isConnectionOwner) && (
              <GoogleSearchConsoleAddButton
                hasConnectedAccounts={hasAccounts}
                isConnecting={isConnecting}
                onSelectExisting={enterEdit}
                onConnectNew={async () => {
                  setIsConnecting(true);
                  try {
                    const url = new URL(window.location.href);
                    url.searchParams.set("sidebarOpen", "true");
                    url.searchParams.set("gscEdit", "true");
                    await connectNewAccount(url.toString());
                  } catch (error) {
                    console.error("Connection error:", error);
                  } finally {
                    setIsConnecting(false);
                  }
                }}
                className={buttonClassName}
              />
            )}
          </div>
        </div>
      )}

      {!isDomainConfigured && !isEditing && isConnecting ? (
        <CardSkeleton />
      ) : !isDomainConfigured && !isEditing && showEmptyState ? (
        <p className="text-sm text-muted-foreground">
          No Google Search Console connection configured yet
        </p>
      ) : !isDomainConfigured && !isEditing ? null : isEditing ? (
        <Card className="overflow-hidden py-3">
          <div className="px-3 md:px-4 space-y-4">
            <div className="space-y-2">
              <span className="text-base font-medium">Google account</span>
              <Select
                value={draftAccountId ?? ""}
                onValueChange={(value) => {
                  setDraftAccountId(value);
                  setDraftSiteUrl(undefined);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.account_id} value={account.account_id}>
                      {account.account_email ||
                        account.account_name ||
                        account.account_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <span className="text-base font-medium">Site</span>
              <Select
                value={draftSiteUrl ?? ""}
                onValueChange={setDraftSiteUrl}
                disabled={!draftAccountId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {(draftAccountId ? getSitesForAccount(draftAccountId) : []).map(
                    (site) => (
                      <SelectItem key={site.site_url} value={site.site_url}>
                        {site.site_url}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={cancelEdit}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveConnection}
                disabled={!draftAccountId || !draftSiteUrl}
              >
                Save
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden py-3 relative">
          {isDisconnecting && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg z-10">
              <Loader2 className="h-6 w-6 animate-spin text-destructive" />
            </div>
          )}
          <div className="flex items-stretch gap-3 px-3 md:px-4">
            <div className="flex-1 min-w-0 space-y-3">
              <div className="space-y-1">
                <h4 className="text-base font-medium">Google account</h4>
                <p className="text-sm text-muted-foreground">
                  {accountDisplayName || "—"}
                </p>
              </div>
              <div className="space-y-1">
                <h4 className="text-base font-medium">Site</h4>
                <p className="text-sm text-muted-foreground break-all">
                  {siteDisplayName || "—"}
                </p>
              </div>
            </div>

            <div className="w-px bg-gray-200 self-stretch" />

            <div className="flex flex-col gap-2 flex-shrink-0 w-24">
              <IncludeExcludeToggle
                id="gsc-enabled"
                checked={googleSearchConsoleIntegration.is_enabled}
                onCheckedChange={(checked) =>
                  setGoogleSearchConsoleEnabled(Boolean(checked))
                }
                disabled={!isAdmin || !isConnectionOwner}
              />
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={enterEdit}
                disabled={!isAdmin || !isConnectionOwner}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowDisconnectDialog(true)}
                disabled={!isAdmin || isDisconnecting || !isConnectionOwner}
                title="Disconnect Google Search Console from this domain"
              >
                Disconnect
              </Button>
              <ConnectionOwnerNotice
                isOwner={isConnectionOwner}
                connectedBy={googleSearchConsoleIntegration.connectedBy}
                connectedByName={connectedByName}
              />
            </div>
          </div>
        </Card>
      )}

      <AlertDialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disconnect Google Search Console?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect your Google Search Console account. You can
              reconnect it at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {isDisconnecting ? "Disconnecting..." : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});
